import React from "react";
import { Col, Row } from "antd";
import { LoadingSpinner, Card, AccordionV2, EmptyStateV2, Switcher, Tooltip, Icon } from "pepsico-ds";
import SingleSelectDropdown from "../../../components/Dropdown/SingleSelectDropdown";
import ResinLineChart from "../../../components/Charts/ResinChart/ResinLineChart";
import DropdownWithIcons from "../../../components/Modal/DropdownWithIcons";
import { SkeletonList } from "../../../components/Skeleton/ResinSkeleton";
import CmaTable from "../../../components/Table/CmaTable";
import NoData from "../../../components/NoData/NoData";
import reset_icon from "../../../assets/reset_icon.svg";
import { useSPGlobalSource } from "./SPGlobalSourceContext";

// S&P Global source component. S&P Global state, redux selectors, handler
// functions and effects live in SPGlobalSourceContext.js (shared via
// SPGlobalSourceProvider) so that S&P Global logic is fully self-contained;
// this component only renders the "filters" or "content" slice of the S&P
// Global UI depending on `section`, mirroring the CMA source component.
const SPSource = props => {
  const { section } = props;
  const {
    selectedTab,
    filterData, tableData, lineChartData,
    historicalForecastSwitcherIndex, onValueTypeChange,

    // Pricing tab filter props
    geographyList, geographySelected, onGeographyChange,
    productList, productSelected, onProductChange,
    productDescriptionsList, productDescriptionsSelected, onProductDescriptionChange,
    unitList, unitsSelected, onUnitChange,
    pricingFromDates, pricingFromDateSelected, onPricingFromDateChange,
    pricingToDates, pricingToDateSelected, onPricingToDateChange,
    resetButtonHandler,
    renderPricingFilterDropdown,

    // Reports tab filter props
    reportFromDates, reportFromDateSelected, onReportFromDateChange,
    reportToDates, reportToDateSelected, onReportToDateChange,

    // Reports tab content props
    isSpGlobalReportDataLoading,
    renderedSpGlobalReportSections, accordionspGlobalSelectedIndex, pdfReportData,
    renderSection,
    pdfUrl,

    // Pricing tab content props
    isUserDataLoading, isFilterDataLoading, isLineDataLoading, isTableDataLoading,
    linechartRef, tableRef,
    checkPDFDownloaded,
    renderForecastLegend,
    resinChartData,
    resinLineChartActualData,
    resinForecastStartVal,
    resinSeriesLineChartIndex,
    resinFirstForecastYearMonth,
    resinFirstForecastKeysToBeMergedActualLast,
    currencySymbol,
    spGlobalColumns
  } = useSPGlobalSource();

  return (
    <>
      {/* Pricing Tab Dropdown Options in S&P Global */}
      {section === "filters" && selectedTab === 1 && (
        <div style={{ width: "100%" }}>
          <>
            <Row
              gutter={[10, 10]} justify="start" style={{ width: "100%", marginTop: "12px" }}
            >
              <div>
                <Switcher
                  className=""
                  items={[
                    { label: "Historical", leadingIcon: "edit", showLeadingIcon: false },
                    { label: "Forecast", leadingIcon: "edit", showLeadingIcon: false }
                  ]}
                  selectedItemIndex={historicalForecastSwitcherIndex}
                  onUpdate={val => { onValueTypeChange(val); }}
                  style={{ marginRight: "0.5rem" }}
                />
                <Tooltip direction="right"
                  text={(
                    <div>
                      <div><strong>Historical:</strong> View actual historical prices for Food & Agriculture products, including oils, grains, oilseeds, and related commodities.</div>
                      <div><strong>Forecast:</strong> View historical and projected prices for the same products based on S&P Global market forecasts (for next 3+ years).</div>
                    </div>
                  )}>
                  <Icon alt="" icon="info" size="small" style={{ marginRight: "10px" }} />
                </Tooltip>
              </div>
            </Row>
            <Row
              gutter={[10, 0]}
              justify="start"
              wrap={false}
              style={{
                width: "100%",
                marginTop: "12px",
                alignItems: "flex-end",
                flexWrap: "nowrap"
              }}
            >
              {renderPricingFilterDropdown("Product", productList, productSelected, onProductChange, { flex: "1" })}
              {renderPricingFilterDropdown("Geography", geographyList, geographySelected, onGeographyChange, { flex: "1" })}
              {renderPricingFilterDropdown("Product Concept", productDescriptionsList, productDescriptionsSelected, onProductDescriptionChange, { flex: "1" }, true)}
              {renderPricingFilterDropdown("Unit of Measure", unitList, unitsSelected, onUnitChange, { flex: "1" })}
              <Col flex="1">
                <SingleSelectDropdown
                  className="buy-con-dropdown-modal cma-dropdown"
                  label="From Date"
                  childList={pricingFromDates}
                  selectedValue={pricingFromDateSelected}
                  setSelectedValue={onPricingFromDateChange}
                />
              </Col>
              <Col flex="1">
                <SingleSelectDropdown
                  className="buy-con-dropdown-modal cma-dropdown"
                  label="To Date"
                  childList={pricingToDates}
                  selectedValue={pricingToDateSelected}
                  setSelectedValue={onPricingToDateChange}
                />
              </Col>
            </Row>
            <Row justify="end">
              <button className="reset-button" onClick={resetButtonHandler} style={{ marginTop: '12px', marginRight: '0.8rem' }}>
                <img src={reset_icon} className="reset_icon" alt="Reset Icon" />
                <span>Reset Filters</span>
              </button>
            </Row>
          </>
        </div>
      )}

      {/* Reports Tab Dropdown Options in S&P Global */}
      {section === "filters" && selectedTab === 2 && (
        <div style={{ display: "flex", justifyContent: "flex-start", width: "100%" }}>
          <Row gutter={[10, 10]} justify="start" style={{ width: "100%", marginTop: "12px" }}>
            <Col xs={24} sm={24} md={5} lg={5} xl={5}>
              <SingleSelectDropdown
                className="buy-con-dropdown-modal"
                label="From Date"
                childList={reportFromDates}
                selectedValue={reportFromDateSelected}
                setSelectedValue={onReportFromDateChange}
              />
            </Col>
            <Col xs={24} sm={24} md={5} lg={5} xl={5}>
              <SingleSelectDropdown
                className="buy-con-dropdown-modal"
                label="To Date"
                childList={reportToDates}
                selectedValue={reportToDateSelected}
                setSelectedValue={onReportToDateChange}
              />
            </Col>
          </Row>
        </div>
      )}

      {/* Reports Content in S&P Global */}
      {section === "content" && selectedTab === 2 && (
        <Row justify={"start"} style={{ width: "100%" }}>
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
            <Card style={{ height: "100%", padding: "24px", width: "100%" }}>
              {isSpGlobalReportDataLoading ? (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: 300 }}>
                  <LoadingSpinner size={{ height: "80px", width: "80px" }} />
                </div>
              ) : renderedSpGlobalReportSections?.length > 0 ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "relative",
                    width: "100%", minHeight: '625px'
                  }}
                >
                  <div className="sba-cci-reports-section" style={{ width: "365px" }}>
                    <div className="sba-cci-reports-section-report-title">
                      Repository
                    </div>
                    <div className="sba-cci-reports-accordion-wrap">
                      <AccordionV2
                        className=""
                        onClickAdd={function wRe() {}}
                        onUpdate={function wRe() {}}
                        presentation="box-shadow"
                        selectedItemIndex={accordionspGlobalSelectedIndex}
                        style={{ position: "relative", width: "314px" }}
                      >
                        {renderedSpGlobalReportSections.map(s =>
                          renderSection(pdfReportData[s.key], s.label)
                        )}
                      </AccordionV2>
                    </div>
                  </div>

                  <div
                    className="sba-cci-pdf-section"
                    style={
                      pdfUrl == null
                        ? { display: "flex", justifyContent: "center", alignItems: "center" }
                        : {}
                    }
                  >
                    {pdfUrl == null ? (
                      <div>
                        <EmptyStateV2
                          className=""
                          description="Select a report from the left panel to preview it here."
                          icon="insert_drive_file"
                          style={{ minHeight: "300px", display: "flex", justifyContent: "center", alignItems: "center" }}
                          titleContent="Pick a report to preview"
                        />
                      </div>
                    ) : (
                      <iframe
                        src={`https://mozilla.github.io/pdf.js/web/viewer.html?file=${encodeURIComponent(pdfUrl)}`}
                        width="100%"
                        height="500px"
                        style={{ border: "none", height: "100%" }}
                        title="PDF Viewer"
                        loading="eager"
                        allow="cross-origin-isolated"
                      />
                    )}
                  </div>
                </div>
              ) : (
                <EmptyStateV2
                  className=""
                  description="No reports available for the selected category"
                  icon="mobiledata_off"
                  style={{ minHeight: "300px", display: "flex", justifyContent: "center", alignItems: "center" }}
                />
              )}
            </Card>
          </Col>
        </Row>
      )}

      {/* Pricing Content in S&P Global */}
      {section === "content" && selectedTab === 1 && (
        <>
        <Row justify={"start"} style={{ width: "100%" }}>
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
          <div ref={linechartRef}>
            <Card style={{ height: "100%", padding: "24px", width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-start", position: "relative", width: "100%" }}>
                {(isUserDataLoading || isFilterDataLoading || isLineDataLoading) ? (
                  <div className="sba-cci-linechart-section">
                    <SkeletonList count={10} height="30" />
                  </div>
                ) : (
                  <div className="sba-cci-linechart-section">
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%", background: "#fff" }}>
                      <div
                        className="resin_linechart_header"
                        style={{ width: "100%", alignItems: "center", textAlign: "left", display: "flex", justifyContent: "space-between", fontSize: "16px" }}
                      >
                        <span className="cma-charts-title">Price Trend</span>
                        <span>
                          <DropdownWithIcons modalRef={linechartRef} kpiName={"Price Trend"} checkPDFDownloaded={checkPDFDownloaded} />
                        </span>
                      </div>
                      <div style={{ marginTop: "-10px" }}>
                        {geographyList?.length > 0 && (
                          <span>
                            <span style={{ fontWeight: "700" }}>Historical / Forecast:</span> {historicalForecastSwitcherIndex == 0 ? 'Historical' : 'Forecast'} &nbsp; &nbsp;| &nbsp; &nbsp;
                            <span style={{ fontWeight: "700" }}>Product:</span> {productSelected?.[0]?.displayText || "-"} &nbsp; &nbsp;| &nbsp; &nbsp;
                            <span style={{ fontWeight: "700" }}>Geography:</span> {geographySelected?.[0]?.displayText || "-"} &nbsp; &nbsp;| &nbsp; &nbsp;                          
                            <br />
                            <div style={{ marginTop: "10px" }}>
                              <span style={{ fontWeight: "700" }}>Product Concept:</span> {productDescriptionsSelected?.[0]?.displayText || "-"} &nbsp; &nbsp;| &nbsp; &nbsp;
                              <span style={{ fontWeight: "700" }}>Unit Of Measure:</span> {unitsSelected?.[0]?.displayText || "-"}
                            </div>
                          </span>
                        )}
                      </div>
                      {renderForecastLegend()}
                      {geographyList?.length > 0 && lineChartData?.series?.length > 0 && (lineChartData?.actual?.length > 0 || lineChartData?.forecast?.length > 0) ? (
                        <ResinLineChart
                          rawData={resinChartData}
                          lineChartactualData={resinLineChartActualData}
                          forecastStartLabel={resinForecastStartVal}
                          risiSeriesLineChartIndex={resinSeriesLineChartIndex}
                          firstForecastYearMonth={resinFirstForecastYearMonth}
                          firstForecastKeysToBeMergedActualLAst={resinFirstForecastKeysToBeMergedActualLast}
                          currencySymbol={currencySymbol}
                          isShowProductInTooltip={true}
                        />
                      ) : <NoData />}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>
          </Col>
        </Row>
        <Row justify={"start"} style={{ width: "100%" }}>
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
            <div ref={tableRef}>
            <Card style={{ height: "100%", padding: "24px", width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-start", position: "relative", width: "100%" }}>
                {(isUserDataLoading || isFilterDataLoading || isTableDataLoading) ? (
                  <div className="sba-cci-barchart-section">
                    <SkeletonList count={10} height="30" />
                  </div>
                ) : (
                  <div className="sba-cci-barchart-section">
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%", background: "#fff" }}>
                      <div
                        className="resin_barchart_header"
                        style={{ width: "100%", alignItems: "center", textAlign: "left", display: "flex", justifyContent: "space-between", fontSize: "16px" }}
                      >
                        <span className="cma-charts-title">Product Price Details</span>
                        <span>
                          <DropdownWithIcons modalRef={tableRef} kpiName={"Product Price Details"} checkPDFDownloaded={checkPDFDownloaded} isTable={true} />
                        </span>
                      </div>

                      <div style={{ marginTop: "-18px" }}>
                        {geographyList?.length > 0 && (
                          <span>
                            <span style={{ fontWeight: "700" }}>Historical / Forecast:</span> {historicalForecastSwitcherIndex == 0 ? 'Historical' : 'Forecast'} &nbsp; &nbsp;| &nbsp; &nbsp;
                            <span style={{ fontWeight: "700" }}>Product:</span> {productSelected?.[0]?.displayText || "-"} &nbsp; &nbsp;| &nbsp; &nbsp;
                            <span style={{ fontWeight: "700" }}>Geography:</span> {geographySelected?.[0]?.displayText || "-"} &nbsp; &nbsp;| &nbsp; &nbsp;
                            <br />
                            <div style={{ marginTop: "10px" }}>
                              <span style={{ fontWeight: "700" }}>Product Concept:</span> {productDescriptionsSelected?.[0]?.displayText || "-"} &nbsp; &nbsp;| &nbsp; &nbsp;
                              <span style={{ fontWeight: "700" }}>Unit Of Measure:</span> {unitsSelected?.[0]?.displayText || "-"}
                            </div>
                          </span>
                        )}
                      </div>

                      {geographyList?.length > 0 && tableData?.ProductPriceDetails && tableData?.ProductPriceDetails?.length > 0 ? (
                        <CmaTable tableData={tableData?.ProductPriceDetails} cmaColumns={spGlobalColumns} />
                      ) : <NoData />}
                    </div>
                  </div>
                )}
              </div>
            </Card>
            </div>
          </Col>
        </Row>
        </>
      )}
    </>
  );
};

export default SPSource;
